import React, { useState, useRef } from 'react';
import { Motor, FilterState } from '../types';
import * as XLSX from 'xlsx';
import { Button, ButtonDivider, Toolbar, ToolbarGroup } from './ButtonGroup';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';


interface MotorDatabaseTabProps {
  motors: Motor[];
  onMotorsChange: (motors: Motor[]) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const MotorDatabaseTab: React.FC<MotorDatabaseTabProps> = ({ motors, onMotorsChange, showStatus }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  
  // 通用的无边框输入框样式
  const seamlessInputStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: colors.text,
    fontSize: '14px',
    width: 'auto',
    minWidth: '20px',
    padding: '2px',
  };
  
  const [filters, setFilters] = useState<FilterState>({
    brand: '',
    kvRange: '',
    thrustMin: undefined,
    weightRange: ''
  });
  const [selectedMotors, setSelectedMotors] = useState<Set<string>>(new Set());
  const [filteredMotors, setFilteredMotors] = useState<Motor[]>(motors);
  const [editingCell, setEditingCell] = useState<{ motorId: string; field: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 应用筛选条件
  React.useEffect(() => {
    let filtered = [...motors];

    if (filters.brand) {
      filtered = filtered.filter(motor => motor.brand === filters.brand);
    }

    if (filters.kvRange) {
      const [min, max] = filters.kvRange.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(motor => motor.kv >= min && motor.kv <= max);
      } else {
        filtered = filtered.filter(motor => motor.kv >= min);
      }
    }

    if (filters.thrustMin && filters.thrustMin > 0) {
      filtered = filtered.filter(motor => motor.maxThrust >= filters.thrustMin!);
    }

    if (filters.weightRange) {
      const [min, max] = filters.weightRange.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(motor => motor.weight >= min && motor.weight <= max);
      } else {
        filtered = filtered.filter(motor => motor.weight >= min);
      }
    }

    setFilteredMotors(filtered);
  }, [motors, filters]);

  const handleFilterChange = (field: keyof FilterState, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      brand: '',
      kvRange: '',
      thrustMin: undefined,
      weightRange: ''
    });
    showStatus(t('motor.messages.filterCleared'));
  };

  const addMotor = () => {
    const newMotor: Motor = {
      id: Date.now().toString(),
      brand: '',
      model: '',
      kv: 0,
      maxPower: 0,
      weight: 0,
      diameter: 0,
      height: 0,
      recommendedProp: '',
      maxThrust: 0,
      maxCurrent: 0,
      price: 0,
      notes: ''
    };
    onMotorsChange([...motors, newMotor]);
    showStatus(t('motor.messages.motorAdded'));
  };

  const deleteSelectedMotors = () => {
    const remainingMotors = motors.filter(motor => !selectedMotors.has(motor.id!));
    onMotorsChange(remainingMotors);
    setSelectedMotors(new Set());
    showStatus(t('motor.messages.motorsDeleted', { count: selectedMotors.size }));
  };

     const selectAllMotors = (checked: boolean) => {
     if (checked) {
       setSelectedMotors(new Set([...filteredMotors.map(motor => motor.id!)]));
     } else {
       setSelectedMotors(new Set());
     }
   };

  const selectMotor = (motorId: string, checked: boolean) => {
    const newSelected = new Set(selectedMotors);
    if (checked) {
      newSelected.add(motorId);
    } else {
      newSelected.delete(motorId);
    }
    setSelectedMotors(newSelected);
  };

  const updateMotor = (motorId: string, field: keyof Motor, value: string | number) => {
    const updatedMotors = motors.map(motor => {
      if (motor.id === motorId) {
        return { ...motor, [field]: value };
      }
      return motor;
    });
    onMotorsChange(updatedMotors);
  };

  // 计算输入框宽度的函数
  const calculateInputWidth = (value: string | number) => {
    const str = String(value);
    // 基础宽度 + 每个字符的宽度（大约10px）+ 更多padding
    const baseWidth = 30;
    const charWidth = 10;
    const padding = 24;
    return Math.max(baseWidth, str.length * charWidth + padding);
  };

  // 渲染可编辑单元格的通用函数
  const renderEditableCell = (motor: Motor, field: keyof Motor, type: string = 'text', step?: string) => {
    const isEditing = editingCell?.motorId === motor.id && editingCell?.field === field;
    const value = motor[field] as string | number;
    
    return (
      <td 
        className="px-4 py-3 cursor-pointer"
        style={{ 
          color: colors.text,
          fontSize: '14px',
          fontWeight: '400',
        }}
        onClick={() => setEditingCell({ motorId: motor.id!, field })}
      >
        {isEditing ? (
          <input
            type={type}
            step={step}
            value={value}
            onChange={(e) => {
              const newValue = type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value;
              updateMotor(motor.id!, field, newValue);
            }}
            onBlur={() => setEditingCell(null)}
            onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
            className="px-2 py-1"
            style={{
              ...seamlessInputStyle,
              width: `${calculateInputWidth(value)}px`,
            }}
            aria-label={`${t('common.edit')} ${field}`}
            autoFocus
          />
        ) : (
          <span>{value}</span>
        )}
      </td>
    );
  };

  // 切换电机的分析状态
  const toggleAnalysisStatus = (motorId: string) => {
    const updatedMotors = motors.map(motor => {
      if (motor.id === motorId) {
        return { ...motor, includeInAnalysis: !motor.includeInAnalysis };
      }
      return motor;
    });
    onMotorsChange(updatedMotors);
    
    const motor = motors.find(m => m.id === motorId);
    const newStatus = !motor?.includeInAnalysis;
    showStatus(t(newStatus ? 'motor.messages.addedToAnalysis' : 'motor.messages.removedFromAnalysis', { 
      name: `${motor?.brand} ${motor?.model}` 
    }));
  };

  // 批量添加到分析
  const addSelectedToAnalysis = () => {
    const updatedMotors = motors.map(motor => {
      if (selectedMotors.has(motor.id!)) {
        return { ...motor, includeInAnalysis: true };
      }
      return motor;
    });
    onMotorsChange(updatedMotors);
    showStatus(t('motor.messages.batchAddedToAnalysis', { count: selectedMotors.size }));
  };

  // 批量从分析中移除
  const removeSelectedFromAnalysis = () => {
    const updatedMotors = motors.map(motor => {
      if (selectedMotors.has(motor.id!)) {
        return { ...motor, includeInAnalysis: false };
      }
      return motor;
    });
    onMotorsChange(updatedMotors);
    showStatus(t('motor.messages.batchRemovedFromAnalysis', { count: selectedMotors.size }));
  };

  // 统计参与分析的电机数量
  const analysisMotorsCount = motors.filter(motor => motor.includeInAnalysis).length;



  const exportToExcel = () => {
    const headers = [
      t('motor.headers.brand'),
      t('motor.headers.model'),
      t('motor.headers.kv'),
      t('motor.headers.maxPower'),
      t('motor.headers.weight'),
      t('motor.headers.diameter'),
      t('motor.headers.height'),
      t('motor.headers.recommendedProp'),
      t('motor.headers.maxThrust'),
      t('motor.headers.maxCurrent'),
      t('motor.headers.price'),
      t('motor.headers.notes')
    ];
    const data = [headers];

         motors.forEach(motor => {
       data.push([
         motor.brand,
         motor.model,
         motor.kv.toString(),
         motor.maxPower.toString(),
         motor.weight.toString(),
         motor.diameter.toString(),
         motor.height.toString(),
         motor.recommendedProp,
         motor.maxThrust.toString(),
         motor.maxCurrent.toString(),
         motor.price.toString(),
         motor.notes
       ]);
     });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // 设置列宽
    ws['!cols'] = [
      { wch: 12 }, { wch: 15 }, { wch: 8 }, { wch: 12 }, 
      { wch: 8 }, { wch: 10 }, { wch: 8 }, { wch: 15 }, 
      { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, t('motor.sheetName'));
    XLSX.writeFile(wb, `${t('motor.exportFileName')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    showStatus(t('motor.messages.exported', { count: motors.length }));
  };

  const importFromExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        const expectedHeaders = [
          t('motor.headers.brand'),
          t('motor.headers.model'),
          t('motor.headers.kv'),
          t('motor.headers.maxPower'),
          t('motor.headers.weight'),
          t('motor.headers.diameter'),
          t('motor.headers.height'),
          t('motor.headers.recommendedProp'),
          t('motor.headers.maxThrust'),
          t('motor.headers.maxCurrent'),
          t('motor.headers.price'),
          t('motor.headers.notes')
        ];
        const headers = jsonData[0];

        if (!validateHeaders(headers, expectedHeaders)) {
          throw new Error(t('motor.messages.incorrectFormat'));
        }

                 const newMotors: Motor[] = [];
         for (let i = 1; i < jsonData.length; i++) {
           const row = jsonData[i];
           if (row[0] && row[1]) {
             newMotors.push({
               id: (Date.now() + i).toString(),
               brand: String(row[0] || ''),
               model: String(row[1] || ''),
               kv: parseFloat(String(row[2])) || 0,
               maxPower: parseFloat(String(row[3])) || 0,
               weight: parseFloat(String(row[4])) || 0,
               diameter: parseFloat(String(row[5])) || 0,
               height: parseFloat(String(row[6])) || 0,
               recommendedProp: String(row[7] || ''),
               maxThrust: parseFloat(String(row[8])) || 0,
               maxCurrent: parseFloat(String(row[9])) || 0,
               price: parseFloat(String(row[10])) || 0,
               notes: String(row[11] || '')
             });
           }
         }

        if (newMotors.length > 0) {
          onMotorsChange(newMotors);
          showStatus(t('motor.messages.imported', { count: newMotors.length }));
        } else {
                      throw new Error(t('motor.messages.noValidData'));
        }
      } catch (error) {
        showStatus(t('motor.messages.importError', { error: (error as Error).message }), 'error');
      }
    };
    reader.readAsArrayBuffer(file);

    // 清空文件输入
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateHeaders = (actualHeaders: any[], expectedHeaders: string[]): boolean => {
    if (!actualHeaders || actualHeaders.length < expectedHeaders.length) {
      return false;
    }
    
    for (let i = 0; i < expectedHeaders.length; i++) {
      if (actualHeaders[i] !== expectedHeaders[i]) {
        return false;
      }
    }
    return true;
  };

  const loadDefaultMotors = () => {
    // 重新加载默认电机数据
    import('../data/defaultData').then(({ defaultMotors }) => {
      onMotorsChange(defaultMotors);
      showStatus(t('motor.messages.resetToDefault'));
    });
  };

  const getBrandOptions = () => {
    const brands = [...new Set(motors.map(motor => motor.brand))].filter(Boolean);
    return brands;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 
          className="text-xl font-semibold mb-6"
          style={{ 
            color: colors.text,
            fontSize: '20px',
            fontWeight: '600',
            letterSpacing: '-0.025em'
          }}
        >
          {t('motor.title')}
        </h2>

        {/* 筛选栏 */}
        <div 
          className="rounded-lg p-4 mb-6"
          style={{ 
            backgroundColor: colors.surface,
            border: `1px solid ${colors.borderLight}`
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('motor.brandFilter')}
              </label>
              <select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight,
                  color: colors.text,
                  fontSize: '14px',
                }}
                aria-label={t('motor.brandFilter')}
              >
                <option value="">{t('motor.filter.all')}</option>
                {getBrandOptions().map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('motor.kvRangeFilter')}
              </label>
              <select
                value={filters.kvRange || ''}
                onChange={(e) => handleFilterChange('kvRange', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight,
                  color: colors.text,
                  fontSize: '14px',
                }}
                aria-label={t('motor.kvRangeFilter')}
              >
                <option value="">{t('motor.filter.all')}</option>
                <option value="300-500">300-500</option>
                <option value="500-700">500-700</option>
                <option value="700-900">700-900</option>
              </select>
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('motor.thrustMinFilter')}
              </label>
              <input
                type="number"
                placeholder={t('motor.minValue')}
                value={filters.thrustMin || ''}
                onChange={(e) => handleFilterChange('thrustMin', e.target.value ? parseFloat(e.target.value) : undefined)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight,
                  color: colors.text,
                  fontSize: '14px',
                }}
                aria-label={t('motor.thrustMinFilter')}
              />
            </div>

            <div>
              <label 
                className="block text-xs font-medium mb-2"
                style={{ 
                  color: colors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '11px',
                  fontWeight: '500'
                }}
              >
                {t('motor.weightRangeFilter')}
              </label>
              <select
                value={filters.weightRange || ''}
                onChange={(e) => handleFilterChange('weightRange', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight,
                  color: colors.text,
                  fontSize: '14px',
                }}
                aria-label={t('motor.weightRangeFilter')}
              >
                <option value="">{t('motor.filter.all')}</option>
                <option value="0-80">0-80</option>
                <option value="80-120">80-120</option>
                <option value="120-999">120+</option>
              </select>
            </div>
          </div>
        </div>

        {/* 工具栏 */}
        <Toolbar>
          <ToolbarGroup title={t('motor.dataManagement')}>
            <Button onClick={addMotor} variant="primary">
              {t('motor.addMotor')}
            </Button>
            <Button
              onClick={deleteSelectedMotors}
              disabled={selectedMotors.size === 0}
              variant="danger"
            >
              {t('motor.deleteSelected')}
            </Button>
            <Button onClick={loadDefaultMotors} variant="secondary">
              {t('motor.resetToDefault')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('motor.filterTitle')}>
            <Button onClick={clearFilters} variant="secondary">
              {t('motor.clearFilters')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('motor.importExport')}>
            <Button onClick={exportToExcel} variant="success">
              {t('motor.exportMotorData')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={importFromExcel}
              className="hidden"
              aria-label={t('motor.importExcelFile')}
            />
            <Button onClick={() => fileInputRef.current?.click()} variant="secondary">
              {t('motor.importFromExcel')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('motor.analysisManagement')}>
            <Button
              onClick={addSelectedToAnalysis}
              disabled={selectedMotors.size === 0}
              variant="success"
            >
              {t('motor.addToAnalysis')} ({selectedMotors.size})
            </Button>
            <Button
              onClick={removeSelectedFromAnalysis}
              disabled={selectedMotors.size === 0}
              variant="warning"
            >
              {t('motor.removeFromAnalysis')} ({selectedMotors.size})
            </Button>
          </ToolbarGroup>
        </Toolbar>

        {/* 数据表格 */}
        <div 
          className="rounded-lg overflow-hidden"
          style={{ 
            backgroundColor: colors.background,
            border: 'none'
          }}
        >
          <div className="overflow-x-auto max-h-96">
            <table className="w-full">
              <thead 
                className="sticky top-0 z-10"
                style={{ 
                  backgroundColor: colors.background,
                  borderBottom: `1px solid ${colors.border}`
                }}
              >
                <tr>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={filteredMotors.length > 0 && selectedMotors.size === filteredMotors.length}
                      onChange={(e) => selectAllMotors(e.target.checked)}
                      aria-label={t('motor.selectDeselectAll')}
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.brand')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.model')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.kv')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.maxPower')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.weight')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.diameter')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.height')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.recommendedProp')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.maxThrust')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.maxCurrent')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.price')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.headers.notes')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left"
                    style={{ 
                      fontSize: '12px',
                      fontWeight: '500',
                      color: colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {t('motor.analysisStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredMotors.map((motor) => (
                  <tr 
                    key={motor.id} 
                    className="transition-colors duration-200"
                    style={{
                      backgroundColor: motor.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent',
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.surface;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = motor.includeInAnalysis 
                        ? (colors.success + '10') 
                        : 'transparent';
                    }}
                  >
                    <td 
                      className="px-4 py-3"
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMotors.has(motor.id!)}
                        onChange={(e) => selectMotor(motor.id!, e.target.checked)}
                        aria-label={`${t('motor.selectMotor')} ${motor.brand} ${motor.model}`}
                      />
                    </td>
                    {renderEditableCell(motor, 'brand')}
                    {renderEditableCell(motor, 'model')}
                    {renderEditableCell(motor, 'kv', 'number')}
                    {renderEditableCell(motor, 'maxPower', 'number')}
                    {renderEditableCell(motor, 'weight', 'number')}
                    {renderEditableCell(motor, 'diameter', 'number', '0.1')}
                    {renderEditableCell(motor, 'height', 'number', '0.1')}
                    {renderEditableCell(motor, 'recommendedProp')}
                    {renderEditableCell(motor, 'maxThrust', 'number')}
                    {renderEditableCell(motor, 'maxCurrent', 'number', '0.1')}
                    {renderEditableCell(motor, 'price', 'number')}
                    {renderEditableCell(motor, 'notes')}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleAnalysisStatus(motor.id!)}
                        className="px-3 py-1 text-xs font-medium rounded-full transition-colors"
                        style={{
                          backgroundColor: motor.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20'),
                          color: motor.includeInAnalysis 
                            ? colors.success 
                            : colors.textMuted,
                          border: `1px solid ${motor.includeInAnalysis ? colors.success : colors.textMuted}20`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = motor.includeInAnalysis 
                            ? (colors.success + '30') 
                            : (colors.textMuted + '30');
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = motor.includeInAnalysis 
                            ? (colors.success + '20') 
                            : (colors.textMuted + '20');
                        }}
                      >
                        {motor.includeInAnalysis ? '参与' : '不参与'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div 
          className="mt-6 text-sm flex items-center gap-4"
          style={{ 
            color: colors.textSecondary,
            fontSize: '13px',
            fontWeight: '400'
          }}
        >
          <span>
            {t('motor.filterResults', { 
              showing: filteredMotors.length, 
              total: motors.length 
            })}
          </span>
          {selectedMotors.size > 0 && (
            <span>
              {t('motor.selected')} <span style={{ color: colors.text, fontWeight: '500' }}>{selectedMotors.size}</span> {t('motor.count')}
            </span>
          )}
          <span>
            {t('motor.inAnalysis')}：<span style={{ color: colors.success, fontWeight: '500' }}>{analysisMotorsCount}</span> {t('motor.count')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MotorDatabaseTab; 